import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Upload, FileText, File, Trash2, Loader2, FolderOpen, Target, GraduationCap, Info } from "lucide-react";
import { toast } from "sonner";

const EXT_LABELS = { pdf: "PDF", docx: "DOCX", txt: "TXT", md: "MD" };

export default function Library() {
    const { authHeaders } = useAuth();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const loadDocs = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/outreach/documents`, { headers: authHeaders() });
            setDocs(res.data || []);
        } catch {
            toast.error("Could not load library");
        } finally {
            setLoading(false);
        }
    }, [authHeaders]); // eslint-disable-line

    useEffect(() => { loadDocs(); }, [loadDocs]);

    const uploadFile = async (file) => {
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["pdf", "docx", "txt", "md"].includes(ext)) {
            toast.error("Only PDF, DOCX, TXT, or MD files are supported.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File too big — max 10 MB.");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const res = await axios.post(`${API}/outreach/documents`, fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            setDocs((prev) => [res.data, ...prev]);
            toast.success(`Added ${res.data.filename} · ${res.data.char_count.toLocaleString()} chars`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onFileInput = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) uploadFile(f);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) uploadFile(f);
    };

    const removeDoc = async (id) => {
        try {
            await axios.delete(`${API}/outreach/documents/${id}`, { headers: authHeaders() });
            setDocs((prev) => prev.filter((d) => d.id !== id));
            toast.success("Removed");
        } catch {
            toast.error("Could not remove");
        }
    };

    return (
        <div data-testid="library-page">
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ PRODUCT LIBRARY</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Your product in one place.</h1>
                    <p className="text-zinc-400 mt-2 max-w-2xl">
                        Upload a product sheet, spec doc, or case-study once — reuse it across <span className="text-white">Cold Outreach</span> and <span className="text-white">Coach Mode</span>. The AI reads the extracted text as context and writes about <em>your</em> product, not generic SaaS.
                    </p>
                </div>
            </div>

            {/* Where these are used */}
            <div className="mb-6 border border-zinc-900 bg-[#0c0c0c] p-4 flex flex-wrap items-center gap-6 text-xs font-mono uppercase tracking-widest text-zinc-500">
                <span className="flex items-center gap-2"><Info className="w-3.5 h-3.5 text-signal" />Reusable across:</span>
                <span className="flex items-center gap-2 text-zinc-300"><Target className="w-3.5 h-3.5 text-signal" />Cold Outreach</span>
                <span className="flex items-center gap-2 text-zinc-300"><GraduationCap className="w-3.5 h-3.5 text-signal" />Coach Mode</span>
                <span className="flex items-center gap-2 text-zinc-300"><FolderOpen className="w-3.5 h-3.5 text-signal" />New Reply</span>
            </div>

            {/* Uploader */}
            <label
                onDrop={onDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className={`block border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors mb-8 ${
                    uploading
                        ? "border-signal/60 bg-signal/5 text-signal"
                        : dragOver
                            ? "border-signal bg-signal/10 text-white"
                            : "border-zinc-800 hover:border-signal hover:bg-signal/5 text-zinc-400 hover:text-white"
                }`}
                data-testid="library-upload-zone"
            >
                <input
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    onChange={onFileInput}
                    className="hidden"
                    data-testid="library-file-input"
                    disabled={uploading}
                />
                <div className="flex flex-col items-center gap-2">
                    {uploading ? (
                        <><Loader2 className="w-6 h-6 animate-spin" /> <span className="text-sm">Parsing document…</span></>
                    ) : (
                        <>
                            <Upload className="w-7 h-7" strokeWidth={1.5} />
                            <span className="text-sm font-semibold">Drag and drop or click to upload</span>
                            <span className="text-xs text-zinc-500">PDF · DOCX · TXT · MD · max 10 MB</span>
                        </>
                    )}
                </div>
            </label>

            {/* List */}
            {loading ? (
                <div className="text-zinc-500 text-sm" data-testid="library-loading">Loading library…</div>
            ) : docs.length === 0 ? (
                <div className="border border-zinc-900 border-dashed p-16 text-center text-zinc-500" data-testid="library-empty">
                    <FolderOpen className="w-10 h-10 mx-auto mb-4 text-zinc-700" strokeWidth={1.2} />
                    Your library is empty. Upload a product doc above to get started.
                </div>
            ) : (
                <div className="space-y-3" data-testid="library-list">
                    {docs.map((d) => {
                        const ext = (d.filename.split(".").pop() || "").toLowerCase();
                        const Icon = ext === "pdf" ? FileText : File;
                        const isExpanded = expanded === d.id;
                        return (
                            <div
                                key={d.id}
                                data-testid={`library-doc-${d.id}`}
                                className="border border-zinc-900 bg-[#0c0c0c] rounded-md overflow-hidden"
                            >
                                <div className="flex items-center gap-4 p-4">
                                    <div className="w-11 h-11 rounded-md bg-signal/10 border border-signal/30 flex items-center justify-center flex-shrink-0">
                                        <Icon className="w-5 h-5 text-signal" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium truncate">{d.filename}</div>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                                            {EXT_LABELS[ext] || ext.toUpperCase()} · {Math.round(d.size / 1024)} KB · {d.char_count.toLocaleString()} chars · {new Date(d.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setExpanded(isExpanded ? null : d.id)}
                                        data-testid={`library-preview-${d.id}`}
                                        className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 border border-zinc-800 hover:border-zinc-600 rounded-md transition-colors"
                                    >
                                        {isExpanded ? "Hide preview" : "Preview text"}
                                    </button>
                                    <button
                                        onClick={() => removeDoc(d.id)}
                                        data-testid={`library-remove-${d.id}`}
                                        className="text-zinc-500 hover:text-red-400 p-2 border border-transparent hover:border-red-400/30 rounded-md transition-colors"
                                        title="Remove from library"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                {isExpanded && (
                                    <div className="border-t border-zinc-900 p-4 bg-black/40">
                                        <pre className="text-xs text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
{d.text_preview}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
